import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { getAllWarehouses } from './services';
import { IWarehouse } from './interface';

interface FilterAutocompleteWarehouseProps {
  readonly onWarehouseChange: (value: number | null) => void;
  readonly warehouseId?: number | null;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly isRequired?: boolean;
  readonly warehouseByUser?: boolean;
}

export default function FilterAutocompleteWarehouse({
  disabled,
  onWarehouseChange,
  warehouseId,
  error = false,
  isRequired = false,
  warehouseByUser,
}: FilterAutocompleteWarehouseProps) {
  const { translate } = useLang();
  const [options, setOptions] = useState<readonly IWarehouse[]>([]);
  const [value, setValue] = useState<IWarehouse | null>(null);
  const loading = useHookstate(false);

  useEffect(() => {
    loading.set(true);
    getAllWarehouses({ warehouseByUser: warehouseByUser }).then((warehouses) => {
      setOptions(warehouses);
      loading.set(false);
    }).catch(() => {
      loading.set(false);
    });
  }, []);

  useEffect(() => {
    if (!warehouseId) {
      setValue(null);
    } else {
      const option = options.find((option) => option.id === warehouseId);
      setValue(option ?? null);
    }
  }, [options, warehouseId]);

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        id="filter-warehouse"
        options={options}
        disabled={disabled}
        value={value}
        noOptionsText={translate('No options')}
        size="small"
        loading={loading.get()}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name ?? ''}
        onChange={(_, warehouse) => {
          setValue(warehouse);
          onWarehouseChange(warehouse?.id ?? null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${translate('warehouse')}${isRequired ? ' *' : ''}`}
            error={error}
          />
        )}
        clearText={translate('Clear')}
      />
    </FormControl>
  );
}
